
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from '@inertiajs/react';
import { Users, UserCheck, Clock, Mail, TrendingUp, FileText, UserMinus } from 'lucide-react';

interface DashboardStats {
  total_users: number;
  active_matches: number;
  pending_reviews: number;
  total_mentors: number;
  total_mentees: number;
  campaigns_sent_this_month: number;
  unmatched_prehealth_mentors_count: number;
  unmatched_graduate_mentors_count: number;
  unmatched_prehealth_mentees_count: number;
  unmatched_graduate_mentees_count: number;
  inactive_mentees_count: number;
}

interface Props {
  stats: DashboardStats;
}

export default function Index({ stats }: Props) {
  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Total Users */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_users}</div>
              <p className="text-xs text-muted-foreground">
                Excluding admins
              </p>
            </CardContent>
          </Card>

          {/* Active Matches */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Matches</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.active_matches}</div>
              <p className="text-xs text-muted-foreground">
                Current mentorship pairs
              </p>
            </CardContent>
          </Card>

          {/* Pending Reviews */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.pending_reviews}</div>
              <p className="text-xs text-muted-foreground">
                Awaiting approval
              </p>
            </CardContent>
          </Card>

          {/* Total Mentors */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Mentors</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_mentors}</div>
              <p className="text-xs text-muted-foreground">
                Verified mentors
              </p>
            </CardContent>
          </Card>

          {/* Total Mentees */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Mentees</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_mentees}</div>
              <p className="text-xs text-muted-foreground">
                Verified mentees
              </p>
            </CardContent>
          </Card>

          {/* Campaigns Sent This Month */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Campaigns This Month</CardTitle>
              <Mail className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.campaigns_sent_this_month}</div>
              <p className="text-xs text-muted-foreground">
                Emails sent
              </p>
            </CardContent>
          </Card>

          {/* Available Pre-Health Mentors */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unmatched Pre-Health Mentors</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unmatched_prehealth_mentors_count}</div>
              <p className="text-xs text-muted-foreground">
                Ready to be matched
              </p>
            </CardContent>
          </Card>

          {/* Available Graduate Mentors */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unmatched Pre-Graduate Mentors</CardTitle>
              <UserCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unmatched_graduate_mentors_count}</div>
              <p className="text-xs text-muted-foreground">
                Ready to be matched
              </p>
            </CardContent>
          </Card>

          {/* Pre-Health Mentees currently visible to mentors */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pre-Health Mentees In Pool</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unmatched_prehealth_mentees_count}</div>
              <p className="text-xs text-muted-foreground">
                Visible to mentors
              </p>
            </CardContent>
          </Card>

          {/* Graduate Mentees currently visible to mentors */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Graduate Mentees In Pool</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.unmatched_graduate_mentees_count}</div>
              <p className="text-xs text-muted-foreground">
                Visible to mentors
              </p>
            </CardContent>
          </Card>

          {/* Explains why the two counts above dropped after the pool gate shipped */}
          <Card className='shadow-none'>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Inactive Mentees</CardTitle>
              <UserMinus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.inactive_mentees_count}</div>
              <p className="text-xs text-muted-foreground">
                Hidden until reactivated
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Link href="/admin/users?status=unverified">
              <Button variant="outline" className="w-full justify-start">
                <UserCheck className="mr-2 h-4 w-4" />
                Review Users
                {stats.pending_reviews > 0 && (
                  <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                    {stats.pending_reviews}
                  </span>
                )}
              </Button>
            </Link>

            <Link href="/admin/matches">
              <Button variant="outline" className="w-full justify-start">
                <Users className="mr-2 h-4 w-4" />
                View Matches
              </Button>
            </Link>

            <Link href="/admin/mentee_pool">
              <Button variant="outline" className="w-full justify-start">
                <UserMinus className="mr-2 h-4 w-4" />
                Mentee Pool
                {stats.inactive_mentees_count > 0 && (
                  <span className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    {stats.inactive_mentees_count}
                  </span>
                )}
              </Button>
            </Link>

            <Link href="/admin/campaigns/new">
              <Button variant="outline" className="w-full justify-start">
                <Mail className="mr-2 h-4 w-4" />
                Create Campaign
              </Button>
            </Link>

            <Link href="/admin/questionnaire_templates">
              <Button variant="outline" className="w-full justify-start">
                <FileText className="mr-2 h-4 w-4" />
                Manage Applications
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

